import { css, CSSObject, SerializedStyles } from "@emotion/react";
import { IVec2 } from "@base/math/Vec2";
import { Is } from "@base/util/Is";
import { mapRecordToArray, PartialRecord } from "@base/util/TypeUtil";
import { ColorUtil } from "./ColorUtil";

export type CssWidthType = "%" | "px" | "auto";
export interface ICssWidth {
  value: number;
  type: CssWidthType;
}

export function cn(...args: any): string | undefined {
  return CssUtil.classNames(...args);
}
export class CssUtil {
  static classNames(...args: any): string | undefined {
    let classes: string[] = [];

    for (const a of args) {
      if (Is.string(a)) classes.push(a);
      else if (Is.array(a) && a.length > 0) {
        const arr = a as string[];
        classes = classes.concat(arr);
      } else if (typeof a === "object") {
        for (const key in a) {
          if (a) {
            const hasOwn = {}.hasOwnProperty;
            if (hasOwn.call(a, key) && a[key]) {
              classes.push(key);
            }
          }
        }
      }
    }
    const ret = classes.join(" ").trim();
    if (!ret) return undefined;
    return ret;
  }

  static px(value: number): string {
    return `${value}px`;
  }

  static percent(value: number): string {
    return `${value}%`;
  }

  static ms(value: number): string {
    const s = value / 1000;
    const ret = `${s}s`;
    return ret;
  }

  static calcDpi(): IVec2 {
    const devicePixelRatio = window.devicePixelRatio || 1;
    const testDiv = document.getElementById("dpi")!;
    return { x: testDiv.offsetWidth * devicePixelRatio, y: testDiv.offsetHeight * devicePixelRatio };
  }

  static colorWithOpacity(hexColor: string, opacity: number): string {
    const rgb = ColorUtil.hexToRgb(hexColor);
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
  }

  static blend(hex1: string, hex2: string, weight: number): string {
    const ra = ColorUtil.hexToRgb(hex1);
    const rb = ColorUtil.hexToRgb(hex2);
    const r = ra.r + (rb.r - ra.r) * weight;
    const g = ra.g + (rb.g - ra.g) * weight;
    const b = ra.b + (rb.b - ra.b) * weight;
    return `rgba(${~~r}, ${~~g}, ${~~b})`;
  }

  static getWidthString(width: ICssWidth): string | undefined {
    switch (width.type) {
      case "%":
        return CssUtil.percent(width.value);
      case "px":
        return CssUtil.px(width.value);
    }
    return undefined;
  }

  static createFontFace(fontFamily: string, urls: PartialRecord<FontTypes, string>, fontWeight: CSSObject["fontWeight"] = "normal", fontStyle: CSSObject["fontStyle"] = "normal"): SerializedStyles {
    console.log("CreateFontFace", fontFamily, urls, fontWeight, fontStyle);
    return css({
      "@font-face": {
        fontFamily,
        src: mapRecordToArray(urls, (from, id) => `url(${from}) format('${id}')`).join(","),
        fontWeight, fontStyle
      }
    })
  }

  static grey(hex1: string): number {
    const c = ColorUtil.hexToRgb(hex1);
    const greyVal = c.r * 0.2126 + c.g * 0.7152 + c.b * 0.0722;
    // console.log("r: ", r, g, b, greyVal);
    return Math.round(greyVal);
  }

}

type FontTypes = "woff2" | "woff" | "ttf" | "woff2-variations" | "truetype-variations" | "truetype";
